import { Form, Formik } from 'formik';
import React, { useState } from 'react';
import * as Yup from 'yup';

import { PrimaryButton } from '@components/Button/variants';
import TextField from '@components/Form/TextField';
import FormErrors from '@components/FormErrors';
import SectionBreakTitle, { SectionColor } from '@components/SectionBreakTitle';
import { useTeamCreate, useTeamUpdate } from '@hooks';
import { TeamSetting } from '@utils/backend-api/teams';

type FormValuesType = {
  name: string;
};

const validationSchema = () =>
  Yup.object({
    name: Yup.string().required('A name is required'),
  });

export default function NameForm({ teamSetting }: { teamSetting: TeamSetting }) {
  const { updateTeamAsync } = useTeamUpdate();
  const { createTeamAsync } = useTeamCreate();
  const [errors, setErrors] = useState([]);
  const { team } = teamSetting;

  const initialValues: FormValuesType = {
    name: team?.name || '',
  };

  const handleSubmit = async (values: FormValuesType, { setSubmitting }) => {
    setErrors([]);

    const resp = team?.id
      ? await updateTeamAsync({
          name: values.name,
        })
      : await createTeamAsync({
          name: values.name,
        });
    if (resp.errors) setErrors(resp.errors);

    setSubmitting(false);
  };

  return (
    <div className="mb-48" data-test="team-settings-name">
      <SectionBreakTitle
        className="mb-32"
        titleClassName="text-primary-magenta"
        color={SectionColor.Magenta}
        title="For your team"
      />
      <div className="mb-24 font-suisse-intl text-description-lg">
        Give your team the information and empower them with the information and connections they
        need to win in tech — if you setup a team account, you can add people to your
        &apos;group&apos;, create a custom signup link for your team, or allow anyone with a valid
        email at your team&apos;s domain to access The Information. To get started, enter your team
        name:
      </div>
      <Formik
        initialValues={initialValues}
        onSubmit={(values, helpers) => handleSubmit(values, helpers)}
        validationSchema={validationSchema}
        enableReinitialize={true}
      >
        {({ isSubmitting, isValid, dirty }) => (
          <Form>
            <TextField name="name" label="Team Name" placeholder="Enter team name" />
            <FormErrors errors={errors} />
            <PrimaryButton
              as="button"
              className="mt-24"
              type="submit"
              disabled={(!isValid && dirty) || isSubmitting}
              isLoading={isSubmitting}
              fullWidth
            >
              {team?.id ? 'Save' : 'Set up team'}
            </PrimaryButton>
          </Form>
        )}
      </Formik>
    </div>
  );
}
